/**
 * Settings — unified settings with 4 sub-tabs.
 *
 * Tabs at /app/settings/{connections,voice,compliance,account}:
 *   connections — Google / Telegram / Email / LLM provider hookups (Setup page)
 *   voice       — ElevenLabs key + voice ID + model
 *   compliance  — recording / consent / DNC enforcement stats
 *   account     — data export, account delete, sign out
 *
 * Connections + Compliance reuse the existing Setup and ComplianceDashboard
 * page bodies directly to avoid duplicating their wiring (OAuth flow,
 * stats queries) — they get their visual refresh in Phase 9 polish.
 */
import { useEffect, useState } from "react";
import {
  NavLink,
  Routes,
  Route,
  Navigate,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { voice, userData } from "../api/client";
import type { VoiceConfig } from "../api/types";
import { useAuth } from "../hooks/useAuth";
import { Button, Card, Mono, Pill } from "../components/v3";
import { Setup } from "./Setup";
import { ComplianceDashboard } from "./ComplianceDashboard";

export function Settings() {
  return (
    <div className="px-8 py-6 max-w-[1280px] mx-auto">
      <div className="mb-6">
        <Mono className="block mb-2">Settings</Mono>
        <h1 className="font-display text-3xl leading-tight">
          The <span className="font-display-italic">few knobs</span> she has.
        </h1>
        <p className="text-t2 mt-2 max-w-[58ch]">
          Connections, voice provider, compliance, and your account.
        </p>
      </div>

      <SubNav />

      <div className="mt-5">
        <Routes>
          <Route index element={<Navigate to="connections" replace />} />
          <Route path="connections" element={<Setup />} />
          <Route path="voice" element={<VoiceTab />} />
          <Route path="compliance" element={<ComplianceDashboard />} />
          <Route path="account" element={<AccountTab />} />
        </Routes>
        <Outlet />
      </div>
    </div>
  );
}

function SubNav() {
  const items = [
    { to: "/app/settings/connections", label: "Connections" },
    { to: "/app/settings/voice", label: "Voice" },
    { to: "/app/settings/compliance", label: "Compliance" },
    { to: "/app/settings/account", label: "Account" },
  ];
  return (
    <div className="flex gap-1 border-b border-line">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          className={({ isActive }) =>
            [
              "relative px-4 py-2.5 text-sm font-medium transition-colors",
              isActive ? "text-t1" : "text-t3 hover:text-t1",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <>
              {it.label}
              {isActive && (
                <span className="absolute bottom-[-1px] left-3 right-3 h-[2px] bg-amber rounded-t-sm" />
              )}
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}

// ── Voice tab ──────────────────────────────────────────────────────────────

function VoiceTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["voice", "config"],
    queryFn: () => voice.getConfig(),
  });

  const cfg: VoiceConfig | undefined = data;
  const [apiKey, setApiKey] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [model, setModel] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    if (!apiKey.trim()) {
      setError("API key is required to save");
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await voice.saveConfig({
        apiKey: apiKey.trim(),
        voiceId: voiceId.trim() || undefined,
        model: model.trim() || undefined,
      });
      setSaved(true);
      setApiKey("");
      qc.invalidateQueries({ queryKey: ["voice", "config"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  if (isLoading) return <div className="skeleton h-48 max-w-[640px]" />;

  return (
    <div className="space-y-4 max-w-[640px]">
      <Card padding="md">
        <div className="flex items-center justify-between mb-4">
          <Mono>ElevenLabs</Mono>
          <Pill tone={cfg?.enabled ? "ok" : "warn"}>
            {cfg?.enabled ? "Configured" : "Not configured"}
          </Pill>
        </div>

        <p className="text-t2 text-sm leading-relaxed mb-5 max-w-[58ch]">
          ElevenLabs powers Vooice's text-to-speech. Get an API key at{" "}
          <a
            href="https://elevenlabs.io"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber hover:underline"
          >
            elevenlabs.io
          </a>
          . Current voice: <span className="text-t1">Matilda</span> on{" "}
          <code className="font-mono text-amber text-xs">eleven_flash_v2_5</code>.
        </p>

        <div className="space-y-3">
          <Field
            label={
              cfg?.enabled
                ? "Replace API key (leave blank to keep)"
                : "API key"
            }
          >
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk_…"
              className={inputCls}
              autoComplete="off"
            />
          </Field>
          <Field label="Voice ID (optional)">
            <input
              type="text"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder={cfg?.voiceId ?? "XrExE9yKIg1WjnnlVkGX"}
              className={inputCls}
            />
          </Field>
          <Field label="Model (optional)">
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={cfg?.model ?? "eleven_flash_v2_5"}
              className={inputCls}
            />
          </Field>
        </div>

        {error && (
          <div className="mt-4 border border-rose/30 bg-rose/[0.06] rounded-md px-3 py-2 text-sm text-rose">
            {error}
          </div>
        )}
        {saved && (
          <div className="mt-4 border border-amber/30 bg-amber/[0.06] rounded-md px-3 py-2 text-sm text-amber">
            Saved.
          </div>
        )}

        <div className="mt-5">
          <Button
            variant="amber"
            size="md"
            onClick={save}
            disabled={saving || !apiKey.trim()}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      <Card padding="md">
        <Mono className="block mb-3">LiveKit</Mono>
        <p className="text-t2 text-sm">
          URL:{" "}
          <code className="font-mono text-amber text-xs">
            {cfg?.livekitUrl ?? "(not configured)"}
          </code>
        </p>
        <p className="text-t3 text-xs mt-2 leading-relaxed">
          Configured via env vars (<code>LIVEKIT_URL</code>,{" "}
          <code>LIVEKIT_API_KEY</code>, <code>LIVEKIT_API_SECRET</code>) — not
          editable in the UI.
        </p>
      </Card>
    </div>
  );
}

// ── Account tab ────────────────────────────────────────────────────────────

function AccountTab() {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [exportJobId, setExportJobId] = useState<string | null>(null);
  const [exportStatus, setExportStatus] = useState<string | null>(null);
  const [exportUrl, setExportUrl] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const [deleteText, setDeleteText] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Poll export status while pending/running
  useEffect(() => {
    if (!exportJobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await userData.getExportStatus(exportJobId);
        if (cancelled) return;
        setExportStatus(res.status);
        if (res.status === "ready") setExportUrl(res.download_url ?? null);
        if (res.status !== "pending" && res.status !== "running") {
          setExporting(false);
        }
      } catch {
        /* keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [exportJobId]);

  async function startExport() {
    setExporting(true);
    setExportStatus(null);
    setExportUrl(null);
    try {
      const result = await userData.requestExport();
      setExportJobId(result.job_id);
      setExportStatus(result.status);
    } catch (e) {
      setExportStatus(e instanceof Error ? e.message : "Failed");
      setExporting(false);
    }
  }

  async function deleteAccount() {
    if (deleteText !== "DELETE MY ACCOUNT") {
      setDeleteError("Type DELETE MY ACCOUNT to confirm.");
      return;
    }
    if (
      !window.confirm(
        "Permanently delete all data? Calls, contacts, memory, audit log — gone. This cannot be undone.",
      )
    )
      return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await userData.deleteAccount(deleteReason || undefined);
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "Delete failed");
      setDeleting(false);
    }
  }

  async function signOut() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="space-y-4 max-w-[640px]">
      <Card padding="md">
        <Mono className="block mb-3">Sign out</Mono>
        <p className="text-t2 text-sm mb-4">
          End the current session on this device.
        </p>
        <Button variant="ghost" size="md" onClick={signOut}>
          Sign out
        </Button>
      </Card>

      <Card padding="md">
        <Mono className="block mb-3">Export your data</Mono>
        <p className="text-t2 text-sm leading-relaxed mb-4 max-w-[58ch]">
          A complete archive of contacts, calls, transcripts, audit log, and
          memory facts. Available for 7 days after the job completes.
        </p>
        <Button
          variant="ghost"
          size="md"
          onClick={startExport}
          disabled={exporting}
        >
          {exporting ? "Preparing…" : "Request export"}
        </Button>
        {exportStatus && (
          <div className="mt-3 text-xs">
            <Mono>Status:</Mono>{" "}
            <span
              className={
                exportStatus === "ready"
                  ? "text-amber"
                  : exportStatus === "failed"
                  ? "text-rose"
                  : "text-t2"
              }
            >
              {exportStatus}
            </span>
          </div>
        )}
        {exportUrl && (
          <a
            href={exportUrl}
            download
            className="inline-block mt-3 text-amber text-sm hover:underline"
          >
            Download archive →
          </a>
        )}
      </Card>

      <Card padding="md" className="border-rose/30">
        <Mono tone="amber" className="block mb-3">
          Delete account
        </Mono>
        <p className="text-t2 text-sm leading-relaxed mb-4 max-w-[58ch]">
          Permanently delete all data — calls, contacts, memory, audit log,
          recordings. The Vooice phone number is released. This cannot be
          undone.
        </p>
        <Field label="Reason (optional)">
          <input
            type="text"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            placeholder="Why are you deleting?"
            className={inputCls}
          />
        </Field>
        <Field label="Type DELETE MY ACCOUNT to confirm">
          <input
            type="text"
            value={deleteText}
            onChange={(e) => setDeleteText(e.target.value)}
            placeholder="DELETE MY ACCOUNT"
            className={`${inputCls} font-mono`}
          />
        </Field>
        {deleteError && (
          <div className="mt-3 text-sm text-rose">{deleteError}</div>
        )}
        <div className="mt-4">
          <Button
            variant="paper"
            size="md"
            onClick={deleteAccount}
            disabled={deleteText !== "DELETE MY ACCOUNT" || deleting}
            className="!bg-rose !border-rose !text-bg disabled:!opacity-50"
          >
            {deleting ? "Deleting…" : "Permanently delete"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ── Form helpers ──────────────────────────────────────────────────────────

const inputCls =
  "w-full px-3 py-2 bg-bg-2 border border-line rounded-md text-sm text-t1 placeholder:text-t4 focus:outline-none focus:border-amber/40 transition-colors";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 last:mb-0">
      <Mono className="block mb-2">{label}</Mono>
      {children}
    </div>
  );
}
